import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { isEmpty, isNumber } from 'lodash';
import { ButtonProps } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useModal } from '@lwtears/lwt-common-frontend/lib/Modal';
import useBookStore from '@monorepo/shared/src/graph/state/query-hooks/use-book-store';
import SvgIconButton from '@monorepo/shared/src/components/common/SvgIconButton';
import useBookParams from '@monorepo/shared/src/hooks/use-book-params';
import { useHTMLRenderEngine } from '@lwtears/lwt-common-frontend/lib/@legacy/HTMLRenderEngine';
import { tA11y } from '@lwtears/lwt-common-frontend/lib/@common/util';
import { useReadBookAudio } from '@lwtears/lwt-common-frontend/lib/ReadBookAudioProvider';
import { useReadAloudPaging, ReadAloudPageType } from '@lwtears/lwt-common-frontend/lib/ReadAloud';
import useTelemetry from '../../../hooks/use-telemetry';
import useReadStoryStore from '../../../graph/state/query-hooks/use-read-story-store';
import { isStudentApp } from '../../../util/app-util';
import { findPageAndPopulatePageData } from '../../../util/book-util';
import { AppRoutes, TelemetryEvents } from '../../../types';
import {
  ApplicationSection,
  Book,
  Character,
  RouteSource,
  PageLayout,
} from '../../../graph/schema-generated-types-educator';
import { formatCharacter } from '../../../util/telemetry-util';

type AccessPageData = {
  book: Book;
  character?: Character | undefined | null;
  isDoublePage: boolean;
  isMainPage: boolean;
  location?: any;
  pages?: any[] | null;
};

type BookNavigationControlProps = {
  navButtonProps?: ButtonProps;
  telemetryData?: AccessPageData;
};

const BookNavigationControl: React.FC<BookNavigationControlProps> = ({
  navButtonProps,
  telemetryData,
}) => {
  const location = useLocation();
  const [, { fireEvent }] = useTelemetry(TelemetryEvents.ACCESS_PAGE);
  const classes = useStyles();
  const bookParams = useBookParams();
  const [{ isOpen }] = useModal();
  const [{ loading: renderingHTML }] = useHTMLRenderEngine();
  const [, { pauseSound }] = useReadBookAudio();
  const [{ book, isLoading: loadingBook, error }] = useBookStore(bookParams);
  const [{ pages, loading: loadingReadStory, error: errorReadStory }] =
    useReadStoryStore(bookParams);
  const [{ disableNextPage, activePages }, { nextPage, previousPage, setTotalPages }] =
    useReadAloudPaging();

  const { className: navButtonClassName, ...restNavButtonProps } = navButtonProps ?? {};

  const createTelemetryEvent = () => {
    if (!telemetryData) return;

    const {
      book,
      character,
      isDoublePage,
      isMainPage,
      location: sourceLocation,
      pages,
    } = telemetryData;

    const page = findPageAndPopulatePageData(pages, activePages[0])?.page;
    // checking if the readAloud modal is open
    // a different event is sent for read aloud
    // in common lib
    if (isOpen || !page || !book) return;

    let foundCharacter = character ? { ...character } : null;

    const route = (sourceLocation?.state as any)?.from || RouteSource.DASHBOARD;

    fireEvent({
      bookId: book.id,
      bookType: book.type,
      bookCode: book.bookCode,
      pageId: page.id,
      pageNumber: isNumber(page.pageNumber) ? `${page.pageNumber}` : ReadAloudPageType.COVER_PAGE,
      pageLayout: isDoublePage ? PageLayout.DOUBLE_PAGE : PageLayout.SINGLE_PAGE,
      section: isMainPage ? ApplicationSection.PRACTICE_BOOKS : ApplicationSection.LETTER_LEARNING,
      ...(foundCharacter &&
        !isEmpty(foundCharacter) && { character: formatCharacter(foundCharacter) }),
      route,
    });
  };

  const handlePreviousPage = () => {
    pauseSound();
    previousPage();
  };

  const handleNextPage = () => {
    pauseSound();
    nextPage();
  };

  useEffect(() => {
    if (!loadingBook && !error && book?.totalPages) {
      if (!isStudentApp) {
        setTotalPages(book.totalPages);
      } else if (!loadingReadStory && !errorReadStory && book?.pages?.length && pages?.length) {
        /* book.pages holds the number of pages in the book, 
          while pages is the number of interactive pages for the activity.
          book.totalPages seems to be some other number that ignores a few pages
          (this unedited number would be used to read the entire book).
          we subtract (book.pages.length - pages.length) from book.totalPages 
          to remove non interactive pages from the end of the book. */
        const pageCountDif = book.pages.length - pages.length;
        setTotalPages(book.totalPages - pageCountDif);
      }
    }
  }, [
    book?.totalPages,
    book?.pages?.length,
    loadingBook,
    error,
    pages?.length,
    loadingReadStory,
    errorReadStory,
  ]);

  useEffect(() => {
    const isActivitySelectionPath = location?.pathname.includes(AppRoutes.ACTIVITY_SELECTION);
    const routeAction = new URLSearchParams(location?.search).get('action') || '';
    const isActivitySelectionModalTransition = routeAction.startsWith(
      `${AppRoutes.ACTIVITY_SELECTION}-`
    );

    if (isActivitySelectionPath || isActivitySelectionModalTransition) return;

    createTelemetryEvent();
  }, [activePages?.[0], telemetryData?.isDoublePage, isOpen, location?.pathname, location?.search]);

  const isLeftNavDisabled = renderingHTML || activePages[0] === ReadAloudPageType.COVER_PAGE;
  const isRightNavDisabled = renderingHTML || disableNextPage;
  return (
    <>
      <SvgIconButton
        ariaLabel={tA11y('buttons.previousPage')}
        onClick={handlePreviousPage}
        iconProps={{ icon: faArrowLeft, size: '3x', ...(!isLeftNavDisabled && { color: 'black' }) }}
        disabled={isLeftNavDisabled}
        className={`${classes.navButton} navigationIconLeft ${navButtonClassName}`}
        {...restNavButtonProps}
      />
      <SvgIconButton
        ariaLabel={tA11y('buttons.nextPage')}
        onClick={handleNextPage}
        iconProps={{
          icon: faArrowRight,
          size: '3x',
          ...(!isRightNavDisabled && { color: 'black' }),
        }}
        disabled={isRightNavDisabled}
        className={`${classes.navButton} navigationIconRight ${navButtonClassName}`}
        {...restNavButtonProps}
      />
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  navButton: {
    border: 'none',
    background: 'transparent',
    zIndex: theme.zIndex.appBar,
    position: 'fixed',
    top: '50%',
    '&.navigationIconLeft': {
      left: 0,
      transform: 'translate(50%, -50%)',
    },
    '&.navigationIconRight': {
      right: 0,
      transform: 'translate(-50%, -50%)',
    },
  },
}));

export default BookNavigationControl;
